"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Tag, Folder, Star, Lock, Truck, ArrowRight, Check, ShieldCheck, RefreshCw, X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const product = products?.[0];

  const galleryImages = React.useMemo(() => {
    if (!product || (!product.featured_image_url && !(product as any).gallery_images)) return [];
    const images: { src: string; alt: string }[] = [];
    
    if (product.featured_image_url) {
      images.push({ src: product.featured_image_url, alt: product.title });
    }

    const rawGallery = (product as any).gallery_images;
    if (Array.isArray(rawGallery)) {
      rawGallery.forEach((img: string, idx: number) => {
        if (img && img !== product.featured_image_url && !images.some(existing => existing.src === img)) {
          images.push({ src: img, alt: `${product.title} gallery ${idx + 1}` });
        }
      });
    }
    
    return images;
  }, [product]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isLightboxOpen || galleryImages.length === 0) return;
    if (e.key === "ArrowLeft") {
      setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    } else if (e.key === "ArrowRight") {
      setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
    } else if (e.key === "Escape") {
      setIsLightboxOpen(false);
    }
  }, [isLightboxOpen, galleryImages.length]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-7 aspect-[4/5] bg-muted animate-pulse" />
        <div className="lg:col-span-5 space-y-8">
          <div className="h-12 w-3/4 bg-muted animate-pulse" />
          <div className="h-8 w-1/4 bg-muted animate-pulse" />
          <div className="h-32 w-full bg-muted animate-pulse" />
          <div className="h-16 w-full bg-muted animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-32">
        <p className="text-muted-foreground mb-6 font-default text-lg">Unable to load product details.</p>
        <button 
          onClick={refetch} 
          className="px-8 py-3 border border-primary hover:bg-primary hover:text-primary-foreground transition-colors font-default uppercase tracking-widest text-sm"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!products || products.length === 0 || !product) {
    return (
      <div className="text-center py-32">
        <p className="text-muted-foreground font-default text-lg">Product not found.</p>
      </div>
    );
  }

  const hasDiscount =
    product.discounted_price !== undefined &&
    product.discounted_price !== null &&
    Number(product.discounted_price) > 0 &&
    Number(product.discounted_price) !== Number(product.price);

  const regularPrice = Number(product.price);
  const salePrice = hasDiscount ? Number(product.discounted_price) : null;

  return (
    <div className="space-y-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <div className="lg:col-span-7 space-y-6">
          <div 
            className="relative group overflow-hidden bg-secondary cursor-zoom-in"
            aria-label="Product gallery"
          >
            <div className="aspect-[4/5] relative">
              {galleryImages.length > 0 && selectedImageIndex >= 0 && selectedImageIndex < galleryImages.length ? (
                <img
                  src={galleryImages[selectedImageIndex]?.src}
                  alt={galleryImages[selectedImageIndex]?.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  onClick={() => setIsLightboxOpen(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <span className="text-muted-foreground font-default uppercase tracking-widest text-xs">Image Unavailable</span>
                </div>
              )}
              
              <div className="absolute top-8 left-8 flex flex-col gap-3">
                <Badge className="bg-accent text-white border-none px-4 py-1.5 uppercase tracking-[0.2em] text-[10px] font-bold shadow-md">
                  New Arrival
                </Badge>
                {hasDiscount && (
                  <Badge className="bg-primary text-white border-none px-4 py-1.5 uppercase tracking-[0.2em] text-[10px] font-bold shadow-md">
                    Limited Offer
                  </Badge>
                )}
              </div>

              <div 
                className="absolute top-8 right-8 bg-black/50 text-white px-3 py-1 text-[10px] uppercase tracking-widest font-bold"
                role="region"
                aria-live="polite"
              >
                {selectedImageIndex + 1} of {galleryImages.length}
              </div>

              <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="bg-white/90 p-3 shadow-lg">
                  <Maximize2 className="size-5 text-primary" />
                </div>
              </div>

              {galleryImages.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 p-2 shadow-md hover:bg-white transition-colors opacity-0 group-hover:opacity-100"
                    aria-label="Previous product image"
                  >
                    <ChevronLeft className="size-6 text-primary" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 p-2 shadow-md hover:bg-white transition-colors opacity-0 group-hover:opacity-100"
                    aria-label="Next product image"
                  >
                    <ChevronRight className="size-6 text-primary" />
                  </button>
                </>
              )}
            </div>
          </div>
          
          {galleryImages.length > 0 && (
            <div className="grid grid-cols-4 gap-4">
              {galleryImages.map((img, i) => (
                <div 
                  key={i} 
                  data-index={i} 
                  tabIndex={0}
                  onClick={() => i >= 0 && i < galleryImages.length && setSelectedImageIndex(i)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedImageIndex(i);
                    }
                  }}
                  className={`aspect-[4/5] overflow-hidden cursor-pointer border transition-all duration-300 bg-secondary ${
                    selectedImageIndex === i ? "border-accent ring-1 ring-accent" : "border-transparent hover:border-accent/50"
                  }`}
                >
                  <img 
                    src={img.src} 
                    alt={img.alt} 
                    className={`w-full h-full object-cover transition-opacity duration-300 ${
                      selectedImageIndex === i ? "opacity-100" : "opacity-60 hover:opacity-100"
                    }`} 
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5 space-y-10 sticky top-8">
          <div className="space-y-4">
            {product.categories && product.categories.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                {product.categories.map((cat, i) => (
                  <Link
                    to={cat.link || "#"}
                    key={cat.id}
                    data-index={i}
                    className="text-[10px] uppercase tracking-[0.3em] text-accent font-bold hover:text-primary transition-colors font-default"
                  >
                    {cat.name}
                    {i < product.categories.length - 1 && <span className="ml-2 text-muted-foreground">/</span>}
                  </Link>
                ))}
              </div>
            )}

            <h1 className="text-5xl md:text-6xl font-bold text-foreground leading-[1.1] tracking-tighter font-['Poppins']">
              {product.title}
            </h1>

            <div className="flex items-center gap-6 flex-wrap pt-2">
              <div className="flex items-baseline gap-4">
                {hasDiscount ? (
                  <>
                    <span className="text-4xl font-bold text-foreground font-['Inter']">
                      ${salePrice?.toFixed(2)}
                    </span>
                    <span className="text-xl text-muted-foreground line-through font-['Inter'] opacity-60">
                      ${regularPrice.toFixed(2)}
                    </span>
                  </>
                ) : (
                  <span className="text-4xl font-bold text-foreground font-['Inter']">
                    ${regularPrice.toFixed(2)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 border-l border-border pl-6">
                <div className="flex text-accent">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="size-3.5 fill-current" />
                  ))}
                </div>
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">(48 Reviews)</span>
              </div>
            </div>
          </div>

          {product.short_description && (
            <div
              className="text-lg text-muted-foreground leading-relaxed font-default max-w-prose"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <div className="space-y-8 pt-4">
            <AddToCartButton
              productId={product.id}
              variant="default"
              size="lg"
              className="w-full"
              buttonClassName="bg-primary text-primary-foreground font-['Poppins'] font-bold py-8 uppercase tracking-[0.2em] hover:bg-primary/90 transition-all shadow-[4px_4px_0px_0px_#C9A227]"
              text="Add to Bag"
              showQuantity={true}
            />

            <div className="grid grid-cols-2 gap-y-8 gap-x-4 pt-6 border-t border-border">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-secondary text-accent">
                  <ShieldCheck className="size-5" strokeWidth={1.25} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest font-bold font-default">Authenticity</span>
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Guaranteed</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-secondary text-accent">
                  <Truck className="size-5" strokeWidth={1.25} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest font-bold font-default">Express</span>
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Worldwide Shipping</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-secondary text-accent">
                  <RefreshCw className="size-5" strokeWidth={1.25} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest font-bold font-default">Returns</span>
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wider">30-Day Window</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="p-3 bg-secondary text-accent">
                  <Lock className="size-5" strokeWidth={1.25} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest font-bold font-default">Secure</span>
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wider">Encrypted Payment</span>
                </div>
              </div>
            </div>
          </div>

          {product.long_description && (
            <div className="pt-10 border-t border-border">
              <h2 className="text-xs uppercase tracking-[0.4em] font-bold text-foreground mb-6 font-['Poppins']">
                The Details
              </h2>
              <div
                className="prose prose-sm max-w-none text-muted-foreground leading-loose [&_p]:mb-4 [&_p:last-child]:mb-0 [&_a]:text-accent [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 font-default"
                dangerouslySetInnerHTML={{ __html: product.long_description }}
              />
            </div>
          )}

          {product.tags && product.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 pt-8 border-t border-border">
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mr-2">
                Keywords:
              </span>
              {product.tags.map((tag, i) => (
                <Link
                  to={tag.link || "#"}
                  key={tag.id}
                  data-index={i}
                  className="text-[10px] uppercase tracking-widest text-foreground hover:text-accent transition-colors font-default border-b border-border pb-0.5"
                >
                  {tag.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {isLightboxOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm animate-in fade-in duration-300"
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
            if (e.key === "ArrowRight") setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
            if (e.key === "Escape") setIsLightboxOpen(false);
          }}
          tabIndex={0}
        >
          <button 
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-8 right-8 text-white/70 hover:text-white transition-colors z-[110]"
          >
            <X className="size-8" />
          </button>

          <button 
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
            }}
            className="absolute left-8 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors z-[110]"
          >
            <ChevronLeft className="size-12" />
          </button>

          <button 
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImageIndex((prev) => (prev + 1) % galleryImages.length);
            }}
            className="absolute right-8 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors z-[110]"
          >
            <ChevronRight className="size-12" />
          </button>

          <div className="relative w-full h-full flex items-center justify-center p-12" onClick={() => setIsLightboxOpen(false)}>
            {galleryImages[selectedImageIndex] && (
              <img
                src={galleryImages[selectedImageIndex].src}
                alt={galleryImages[selectedImageIndex].alt}
                className="max-w-full max-h-full object-contain select-none cursor-default"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-[110]">
            {galleryImages.map((_, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(i);
                }}
                className={`size-2 rounded-lg transition-all duration-300 ${
                  selectedImageIndex === i ? "bg-accent w-8" : "bg-white/30 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Product() {
  const { ref, isVisible } = useRevealOnScroll({ once: true });
  
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section 
      data-section-id="1521"
      id="single-product-template-section"
      className="relative w-full bg-background text-foreground py-20 md:py-32 overflow-hidden"
    >
      <div 
        ref={ref}
        className={`relative max-w-7xl mx-auto px-6 lg:px-12 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <nav className="mb-16 flex items-center gap-4 text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-bold">
          <Link to="/HomePage" className="hover:text-accent transition-colors">
            Home
          </Link>
          <span className="text-border">/</span>
          <Link to="/ShopPage" className="hover:text-accent transition-colors">
            Atelier
          </Link>
          <span className="text-border">/</span>
          <span className="text-foreground">Product</span>
        </nav>

        <EcommerceProductsProvider wp_query={wp_query}>
          <SingleProductConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}